<template>
    <div id="reportIssueModal" class="modal modal-report-issue" role="dialog" @click.self="closeModal">
        <div class="modal-dialog modal-lg">
            <div class="modal-content modal-content-report">
                <div class="modal-header-report">
                    <h4 class="modal-title-report">
                        <i class="ti-flag-alt"></i>
                        Report a Concern
                    </h4>
                    <button type="button" class="close close-report" @click="closeModal()">&times;</button>
                </div>
                
                <div class="modal-body-report" :class="{ 'has-form': !submitted }">
                    <!-- Success Message -->
                    <div v-if="submitted" class="success-container">
                        <div class="success-icon">
                            <i class="ti-check"></i>
                        </div>
                        <h3>Thank you for your report!</h3>
                        <p class="tracking-label">Your tracking number is:</p>
                        <div class="tracking-number-box">{{ trackingNumber }}</div>
                        <p class="tracking-note">Please save this number for future reference.</p>
                        <button class="btn btn-primary btn-submit-another" @click="resetForm()">Submit Another Report</button>
                    </div>

                    <!-- Form -->
                    <form v-else @submit.prevent="submitReport">
                        <!-- Request Type -->
                        <div class="form-group form-section">
                            <label class="form-label">Request Type <span class="required">*</span></label>
                            <select class="form-control" v-model="form.request_type" required>
                                <option value="">Select Type</option>
                                <optgroup label="Technical">
                                    <option value="Bug Report">Bug Report</option>
                                    <option value="Crash Report">Crash Report</option>
                                    <option value="Performance Issue">Performance Issue</option>
                                </optgroup>
                                <optgroup label="Support">
                                    <option value="Question/Help">Question/Help</option>
                                    <option value="Data Issue">Data Issue</option>
                                    <option value="Account Issue">Account Issue</option>
                                </optgroup>
                                <optgroup label="Suggestions">
                                    <option value="Feature Request">Feature Request</option>
                                    <option value="Feedback">Feedback</option>
                                </optgroup>
                            </select>
                            <div v-if="form.request_type" class="request-type-description">
                                <i class="ti-info-alt"></i>
                                {{ requestTypeDescriptions[form.request_type] }}
                            </div>
                        </div>

                        <div class="form-group form-group-row form-section">
                            <div class="row">
                                <!-- Platform -->
                                <div class="col-md-6">
                                    <label class="form-label">Platform <span class="required">*</span></label>
                                    <select class="form-control" v-model="form.platform" required>
                                        <option value="">Select Platform</option>
                                        <option value="Web">Web (This)</option>
                                        <option value="App">Android App</option>
                                        <option value="Both">Both</option>
                                    </select>
                                </div>
                                
                                <!-- Priority -->
                                <div class="col-md-6">
                                    <label class="form-label">Priority <span class="required">*</span></label>
                                    <select class="form-control" v-model="form.priority" required>
                                        <option value="">Select Priority</option>
                                        <option value="Urgent">Urgent</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <!-- Priority Description -->
                        <div v-if="form.priority" class="priority-description" :class="'priority-' + form.priority.toLowerCase()">
                            <strong>{{ form.priority }}:</strong> {{ priorityDescriptions[form.priority] }}
                        </div>

                        <div class="row">
                            <!-- Name -->
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label class="form-label">Your Name <span class="required">*</span></label>
                                    <input 
                                        type="text" 
                                        class="form-control" 
                                        v-model="form.name" 
                                        placeholder="Enter your name"
                                        required
                                    />
                                </div>
                            </div>
                            
                            <!-- Email -->
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label class="form-label">Your Email <span class="required">*</span></label>
                                    <input 
                                        type="email" 
                                        class="form-control form-control-disabled" 
                                        v-model="form.email" 
                                        placeholder="Enter your email"
                                        required
                                        disabled
                                    />
                                    <small class="form-text text-muted">Email from your account</small>
                                </div>
                            </div>
                        </div>

                        <!-- Summary -->
                        <div class="form-group form-section">
                            <label class="form-label">Summary <span class="required">*</span></label>
                            <input 
                                type="text" 
                                class="form-control" 
                                v-model="form.summary" 
                                placeholder="One sentence summary of the bug or request"
                                maxlength="500"
                                required
                            />
                            <small class="form-text text-muted">{{ form.summary.length }}/500 characters</small>
                        </div>

                        <!-- Description -->
                        <div class="form-group form-section">
                            <label class="form-label">Detailed Description <span class="required">*</span></label>
                            <textarea 
                                class="form-control" 
                                v-model="form.description" 
                                rows="4"
                                placeholder="Detailed description including device and browser/OS, screenshots or steps to reproduce"
                                required
                            ></textarea>
                        </div>

                        <!-- File Upload -->
                        <div class="form-group form-section">
                            <label class="form-label">Attachments (Screenshots, files)</label>
                            <div class="file-upload-area" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
                                <input 
                                    type="file" 
                                    ref="fileInput" 
                                    @change="handleFileSelect" 
                                    multiple 
                                    accept="image/*,.pdf,.doc,.docx,.txt"
                                    style="display: none;"
                                />
                                <i class="ti-cloud-up upload-icon"></i>
                                <p class="upload-text">Click to upload or drag and drop files here</p>
                                <p class="upload-hint">Max file size: 10MB. Supported: Images, PDF, DOC, TXT</p>
                            </div>
                            
                            <!-- Uploaded Files List -->
                            <div v-if="uploadedFiles.length > 0" class="uploaded-files-list">
                                <div v-for="(file, index) in uploadedFiles" :key="index" class="uploaded-file-item">
                                    <i class="ti-file"></i>
                                    <span class="file-name">{{ file.name }}</span>
                                    <span v-if="file.uploading" class="uploading-badge">Uploading...</span>
                                    <span v-else-if="file.error" class="error-badge">Failed</span>
                                    <span v-else class="success-badge">Uploaded</span>
                                    <button type="button" class="btn-remove-file" @click="removeFile(index)">
                                        <i class="ti-close"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Diagnostics Checkbox -->
                        <div v-if="showDiagnosticsOption" class="form-group checkbox-group diagnostics-group">
                            <label class="checkbox-label">
                                <input type="checkbox" v-model="form.include_diagnostics" />
                                <span class="checkmark"></span>
                                Include technical diagnostic data to help us fix this faster
                            </label>
                            <a href="#" class="diagnostics-details-toggle" @click.prevent="showDiagnosticsDetails = !showDiagnosticsDetails">
                                <i class="ti-info-alt"></i>
                                {{ showDiagnosticsDetails ? 'Hide details' : "What's included?" }}
                            </a>
                            <div v-if="showDiagnosticsDetails" class="diagnostics-details">
                                <p><strong>We include:</strong></p>
                                <ul>
                                    <li>Browser, OS, screen size, network speed, app version</li>
                                    <li>Current page and recent pages you visited in this session</li>
                                    <li>Recent console log messages and JavaScript errors</li>
                                    <li>Recent failed network requests (URL and status code only)</li>
                                    <li>Recent clicks (which buttons/links, not the text you typed)</li>
                                    <li>A link to your recorded session (LogRocket) for visual replay</li>
                                </ul>
                                <p><strong>We do NOT include:</strong> passwords, form inputs, file contents, request/response bodies.</p>
                            </div>
                        </div>

                        <!-- Send Copy Checkbox -->
                        <div class="form-group checkbox-group">
                            <label class="checkbox-label">
                                <input type="checkbox" v-model="form.send_copy_to_user" />
                                <span class="checkmark"></span>
                                Send me a copy of my responses
                            </label>
                        </div>

                        <!-- Error Message -->
                        <div v-if="errorMessage" class="error-alert">
                            <i class="ti-alert"></i>
                            {{ errorMessage }}
                        </div>
                    </form>
                </div>
                
                <!-- Sticky Footer -->
                <div v-if="!submitted" class="modal-footer-report">
                    <button type="button" class="btn btn-secondary" @click="closeModal()">Cancel</button>
                    <button type="button" class="btn btn-primary btn-submit" :disabled="isSubmitting" @click="submitReport">
                        <span v-if="isSubmitting">
                            <i class="ti-reload rotating"></i> Submitting...
                        </span>
                        <span v-else>
                            <i class="ti-check"></i> Submit Report
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import diagnostics from '@/utils/diagnostics';

const DIAGNOSTIC_REQUEST_TYPES = ['Bug Report', 'Crash Report', 'Performance Issue', 'Data Issue', 'Account Issue'];

export default {
    name: 'ReportIssueModal',
    data() {
        return {
            form: {
                name: '',
                email: '',
                summary: '',
                description: '',
                platform: '',
                request_type: '',
                priority: '',
                send_copy_to_user: true,
                include_diagnostics: true
            },
            uploadedFiles: [],
            isSubmitting: false,
            submitted: false,
            trackingNumber: '',
            errorMessage: '',
            showDiagnosticsDetails: false,
            priorityDescriptions: {
                'Urgent': 'A major functionality is broken, the app is crashing, or there\'s a security vulnerability. No workaround is available.',
                'High': 'A key feature is broken, seriously affecting user experience. A workaround may exist.',
                'Medium': 'The app still works, and a workaround is likely available. Users are inconvenienced but not blocked.',
                'Low': 'Cosmetic or very rare bugs. Does not affect the core user experience.'
            },
            diagnosticRequestTypes: DIAGNOSTIC_REQUEST_TYPES,
            requestTypeDescriptions: {
                'Question/Help': 'General questions about how to use the app.',
                'Data Issue': 'Problems with data sync, missing data, or incorrect data.',
                'Performance Issue': 'App is slow, laggy, or unresponsive.',
                'UI/UX Issue': 'Design problems, layout issues, or confusing interface.',
                'Crash Report': 'App crashes or freezes unexpectedly.',
                'Account Issue': 'Login problems, permissions, or access issues.',
                'Security Concern': 'Security vulnerabilities or suspicious activity.',
                'Documentation': 'Missing or incorrect documentation/help content.',
                'Integration Issue': 'Problems with third-party integrations.',
                'Bug Report': 'Something is not working as expected.',
                'Feature Request': 'Request for a new feature or enhancement.',
                'Feedback': 'General feedback about the app experience.',
                'Suggestion': 'General suggestions to improve the app.',
                'Other': 'Anything that doesn\'t fit the other categories.'
            }
        };
    },
    computed: {
        showDiagnosticsOption() {
            return this.diagnosticRequestTypes.indexOf(this.form.request_type) !== -1;
        }
    },
    watch: {
        'form.request_type'(newType) {
            // Default the diagnostics toggle ON for technical issues, OFF otherwise
            this.form.include_diagnostics = this.diagnosticRequestTypes.indexOf(newType) !== -1;
        }
    },
    methods: {
        openModal() {
            // Pre-fill user data if logged in
            const userData = JSON.parse(localStorage.getItem('userdata'));
            if (userData) {
                this.form.name = userData.userName || '';
                this.form.email = userData.userEmail || '';
            }
            const modal = document.getElementById('reportIssueModal');
            if (modal) {
                modal.style.display = 'flex';
                document.body.style.overflow = 'hidden';
            }
        },
        closeModal() {
            document.getElementById('reportIssueModal').style.display = 'none';
            document.body.style.overflow = 'auto';
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileSelect(event) {
            const files = event.target.files;
            this.uploadFiles(files);
        },
        handleDrop(event) {
            const files = event.dataTransfer.files;
            this.uploadFiles(files);
        },
        async uploadFiles(files) {
            for (let file of files) {
                if (file.size > 10 * 1024 * 1024) {
                    this.errorMessage = `File "${file.name}" exceeds 10MB limit.`;
                    continue;
                }
                
                const fileObj = {
                    name: file.name,
                    uploading: true,
                    error: false,
                    url: null
                };
                this.uploadedFiles.push(fileObj);
                
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    const response = await axios.post('/issue-report/upload/attachment', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    
                    if (response.data.response.error.error_code === 0) {
                        fileObj.uploading = false;
                        fileObj.url = response.data.response.data.src;
                    } else {
                        fileObj.uploading = false;
                        fileObj.error = true;
                    }
                } catch (error) {
                    fileObj.uploading = false;
                    fileObj.error = true;
                    console.error('File upload error:', error);
                }
            }
        },
        removeFile(index) {
            this.uploadedFiles.splice(index, 1);
        },
        async submitReport() {
            this.errorMessage = '';
            
            // Validate form
            if (!this.form.name || !this.form.email || !this.form.summary || 
                !this.form.description || !this.form.platform || 
                !this.form.request_type || !this.form.priority) {
                this.errorMessage = 'Please fill in all required fields.';
                return;
            }
            
            // Wait for all uploads to complete
            const pendingUploads = this.uploadedFiles.filter(f => f.uploading);
            if (pendingUploads.length > 0) {
                this.errorMessage = 'Please wait for all files to finish uploading.';
                return;
            }
            
            this.isSubmitting = true;
            
            try {
                let description = this.form.description;
                let diagnosticAttachmentUrl = null;
                let logRocketUrl = null;

                // Capture + upload diagnostics for technical issue types when opted in
                if (this.showDiagnosticsOption && this.form.include_diagnostics) {
                    try {
                        const snapshot = await diagnostics.snapshot();
                        logRocketUrl = snapshot.logRocketSessionURL || null;
                        diagnosticAttachmentUrl = await this.uploadDiagnosticSnapshot(snapshot);
                    } catch (diagErr) {
                        console.warn('Failed to capture/upload diagnostics:', diagErr);
                        // Non-fatal: continue submitting the report without diagnostics
                    }
                }

                // Prepend a small, admin-visible diagnostic header to description so
                // it shows up in emails and the admin dialog without needing to open
                // the JSON attachment.
                if (logRocketUrl || diagnosticAttachmentUrl) {
                    const headerLines = ['--- Diagnostic data ---'];
                    if (logRocketUrl) headerLines.push('Session replay: ' + logRocketUrl);
                    if (diagnosticAttachmentUrl) headerLines.push('Full snapshot: ' + diagnosticAttachmentUrl);
                    headerLines.push('Page at time of report: ' + location.pathname);
                    headerLines.push('-----------------------');
                    description = headerLines.join('\n') + '\n\n' + description;
                }

                // Collect successful file URLs (includes the diagnostics JSON if uploaded)
                const attachments = this.uploadedFiles
                    .filter(f => f.url)
                    .map(f => f.url);
                
                const payload = {
                    name: this.form.name,
                    email: this.form.email,
                    summary: this.form.summary,
                    description: description,
                    platform: this.form.platform,
                    request_type: this.form.request_type,
                    priority: this.form.priority,
                    attachments: attachments,
                    send_copy_to_user: this.form.send_copy_to_user
                };
                
                const response = await axios.post('/issue-report/submit', payload);
                
                if (response.data.response.error.error_code === 0) {
                    this.trackingNumber = response.data.response.data.tracking_number;
                    this.submitted = true;
                    // Tag the LogRocket session with the tracking number so admins
                    // can search LogRocket by tracking number later.
                    diagnostics.tagLogRocket(this.trackingNumber, {
                        request_type: this.form.request_type,
                        priority: this.form.priority,
                        platform: this.form.platform
                    });
                } else {
                    this.errorMessage = response.data.response.error.error_message || 'Failed to submit report. Please try again.';
                }
            } catch (error) {
                console.error('Submit error:', error);
                this.errorMessage = 'An error occurred while submitting your report. Please try again.';
            } finally {
                this.isSubmitting = false;
            }
        },
        async uploadDiagnosticSnapshot(snapshot) {
            try {
                const json = JSON.stringify(snapshot, null, 2);
                const blob = new Blob([json], { type: 'application/json' });
                const filename = 'diagnostic_' + Date.now() + '.json';
                const file = new File([blob], filename, { type: 'application/json' });

                // Show it in the uploaded list for transparency
                const fileObj = { name: filename, uploading: true, error: false, url: null, isDiagnostic: true };
                this.uploadedFiles.push(fileObj);

                const formData = new FormData();
                formData.append('file', file);
                const response = await axios.post('/issue-report/upload/attachment', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data.response.error.error_code === 0) {
                    fileObj.uploading = false;
                    fileObj.url = response.data.response.data.src;
                    return fileObj.url;
                } else {
                    fileObj.uploading = false;
                    fileObj.error = true;
                    return null;
                }
            } catch (e) {
                console.warn('Diagnostic upload failed:', e);
                return null;
            }
        },
        resetForm() {
            this.form = {
                name: '',
                email: '',
                summary: '',
                description: '',
                platform: '',
                request_type: '',
                priority: '',
                send_copy_to_user: true,
                include_diagnostics: true
            };
            this.showDiagnosticsDetails = false;
            this.uploadedFiles = [];
            this.submitted = false;
            this.trackingNumber = '';
            this.errorMessage = '';
            
            // Re-fill user data
            const userData = JSON.parse(localStorage.getItem('userdata'));
            if (userData) {
                this.form.name = userData.userName || '';
                this.form.email = userData.userEmail || '';
            }
        }
    },
    mounted() {
        // Listen for event to open modal
        window.Bus.$on('open-report-issue-modal', () => {
            this.openModal();
        });
    },
    beforeDestroy() {
        window.Bus.$off('open-report-issue-modal');
    }
};
</script>

<style lang="scss">
.modal-report-issue {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    background-color: rgba(0, 0, 0, 0.6);
    padding: 20px;
    display: none;
}

.modal-report-issue {
    align-items: center;
    justify-content: center;
}

.modal-report-issue .modal-dialog {
    max-width: 700px;
    width: 100%;
    margin: 0 auto;
}

.modal-content-report {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 40px);
}

.modal-header-report {
    background: #1e88e5;
    color: white;
    padding: 20px 25px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-title-report {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
}

.close-report {
    background: none;
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
}

.modal-body-report {
    padding: 28px 32px;
    overflow-y: auto;
    flex: 1;
    
    &.has-form {
        padding-bottom: 20px;
    }
}

.form-group {
    margin-bottom: 20px;
}

.form-section {
    margin-bottom: 32px;
    padding-bottom: 8px;
}

.form-group-row {
    margin-bottom: 36px;
    
    .row {
        margin-bottom: 0;
    }
}

.form-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
    font-size: 14px;
}

.required {
    color: #dc3545;
}

.form-control {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
    background-color: #fff;
    
    &:focus {
        border-color: #1e88e5;
        box-shadow: 0 0 0 3px rgba(30, 136, 229, 0.1);
        outline: none;
    }
}

.col-md-6 {
    flex: 0 0 50%;
    padding: 0 12px;
    box-sizing: border-box;
}

.col-md-4 {
    flex: 0 0 33.333%;
    padding: 0 12px;
    box-sizing: border-box;
}

.form-control-disabled {
    background-color: #f5f5f5;
    color: #666;
    cursor: not-allowed;
}

textarea.form-control {
    resize: vertical;
    min-height: 120px;
}

.form-text {
    display: block;
    margin-top: 6px;
    font-size: 12px;
}

.row {
    display: flex;
    margin: 0 -12px;
    flex-wrap: wrap;
    
    & + .form-group {
        margin-top: 4px;
    }
}

select.form-control {
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 10px center;
    background-repeat: no-repeat;
    background-size: 20px;
    padding-right: 40px;
}

.priority-description {
    padding: 12px 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 13px;
    border-left: 4px solid;
    
    &.priority-urgent {
        background: #ffeef0;
        border-color: #dc3545;
        color: #721c24;
    }
    
    &.priority-high {
        background: #fff3e0;
        border-color: #fd7e14;
        color: #8a4100;
    }
    
    &.priority-medium {
        background: #fff8e1;
        border-color: #ffc107;
        color: #856404;
    }
    
    &.priority-low {
        background: #e8f5e9;
        border-color: #28a745;
        color: #155724;
    }
}

.request-type-description {
    margin-top: 10px;
    padding: 10px 14px;
    background: #e3f2fd;
    border-radius: 6px;
    font-size: 13px;
    color: #1565c0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    i {
        font-size: 16px;
        flex-shrink: 0;
    }
}

.file-upload-area {
    border: 2px dashed #d0d0d0;
    border-radius: 10px;
    padding: 28px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    background: #fafafa;
    
    &:hover {
        border-color: #1e88e5;
        background: #f0f7ff;
    }
    
    .upload-icon {
        font-size: 32px;
        color: #1e88e5;
        display: block;
        margin-bottom: 12px;
    }
    
    .upload-text {
        display: block;
        margin: 0 0 8px;
        color: #333;
        font-weight: 500;
        font-size: 14px;
    }
    
    .upload-hint {
        display: block;
        margin: 0;
        color: #999;
        font-size: 12px;
        font-weight: 400;
    }
}

.uploaded-files-list {
    margin-top: 15px;
}

.uploaded-file-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 8px;
    
    i {
        color: #1e88e5;
        margin-right: 10px;
    }
    
    .file-name {
        flex: 1;
        font-size: 13px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    
    .uploading-badge {
        font-size: 11px;
        padding: 3px 8px;
        background: #ffc107;
        color: #333;
        border-radius: 4px;
        margin-left: 10px;
    }
    
    .success-badge {
        font-size: 11px;
        padding: 3px 8px;
        background: #28a745;
        color: white;
        border-radius: 4px;
        margin-left: 10px;
    }
    
    .error-badge {
        font-size: 11px;
        padding: 3px 8px;
        background: #dc3545;
        color: white;
        border-radius: 4px;
        margin-left: 10px;
    }
    
    .btn-remove-file {
        background: none;
        border: none;
        color: #dc3545;
        cursor: pointer;
        margin-left: 10px;
        padding: 5px;
        opacity: 0.7;
        
        &:hover {
            opacity: 1;
        }
    }
}

.checkbox-group {
    margin-top: 12px;
    margin-bottom: 24px;
    padding-top: 8px;
}

.checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    
    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        margin-right: 10px;
        cursor: pointer;
    }
}

.diagnostics-group {
    background: #f3f8ff;
    border: 1px solid #d6e4ff;
    border-radius: 8px;
    padding: 12px 15px;
    margin-bottom: 20px;
}

.diagnostics-details-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #1e88e5;
    text-decoration: none;
    margin-top: 6px;
    margin-left: 28px;

    &:hover {
        text-decoration: underline;
    }

    i {
        font-size: 13px;
    }
}

.diagnostics-details {
    margin-top: 10px;
    margin-left: 28px;
    padding: 10px 12px;
    background: #fff;
    border: 1px dashed #b8d4ff;
    border-radius: 6px;
    font-size: 12px;
    color: #444;

    p {
        margin: 0 0 6px 0;
    }

    ul {
        margin: 0 0 8px 18px;
        padding: 0;

        li {
            margin-bottom: 2px;
        }
    }
}

.error-alert {
    background: #ffeef0;
    border: 1px solid #dc3545;
    color: #721c24;
    padding: 12px 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    
    i {
        font-size: 18px;
    }
}

.modal-footer-report {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 32px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    flex-shrink: 0;
}

.btn {
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.btn-secondary {
    background: #f8f9fa;
    border: 1px solid #ddd;
    color: #333;
    
    &:hover {
        background: #e9ecef;
    }
}

.btn-primary {
    background: #1e88e5;
    border: none;
    color: white;
    
    &:hover {
        background: #1976d2;
    }
    
    &:disabled {
        background: #90caf9;
        cursor: not-allowed;
    }
}

.rotating {
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Success Container */
.success-container {
    text-align: center;
    padding: 40px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.success-container .success-icon {
    width: 80px;
    height: 80px;
    background: #28a745;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
}

.success-container .success-icon i {
    font-size: 40px;
    color: white;
}

.success-container h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 24px;
}

.success-container .tracking-label {
    color: #666;
    margin-bottom: 10px;
    font-size: 16px;
}

.success-container .tracking-number-box {
    background: #e3f2fd;
    color: #1e88e5;
    font-size: 24px;
    font-weight: bold;
    padding: 15px 30px;
    border-radius: 8px;
    display: block;
    margin: 10px 0;
    letter-spacing: 1px;
}

.success-container .tracking-note {
    font-size: 13px;
    color: #888;
    margin-top: 10px;
}

.success-container .btn-submit-another {
    margin-top: 25px;
}

@media (max-width: 768px) {
    .modal-dialog {
        margin: 10px;
    }
    
    .modal-body-report {
        padding: 20px;
    }
    
    .modal-footer-report {
        padding: 16px 20px;
        flex-direction: column-reverse;
        
        .btn {
            width: 100%;
            justify-content: center;
        }
    }
    
    .col-md-6,
    .col-md-4 {
        flex: 0 0 100%;
    }
}
</style>
